export type LocalProfile = {
  games_played: number;
  current_streak: number;
  max_streak: number;
  high_score: number;
  last_played_date: string; // YYYY-MM-DD (local calendar date)
};

const STORAGE_VERSION = 'v1';

function key(userId: string) {
  return `centumth_profile_${STORAGE_VERSION}_${userId}`;
}

function empty(): LocalProfile {
  return {
    games_played: 0,
    current_streak: 0,
    max_streak: 0,
    high_score: 0,
    last_played_date: '',
  };
}

const pad2 = (n: number) => String(n).padStart(2, '0');
export function formatLocalYmd(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYmdToLocalDate(ymd: string) {
  const [y, m, day] = ymd.split('-').map((x) => Number(x));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return null;
  if (y < 1970 || m < 1 || m > 12 || day < 1 || day > 31) return null;
  return new Date(y, m - 1, day); // local midnight
}

export function getLocalProfile(userId: string): LocalProfile {
  if (typeof window === 'undefined') return empty();
  try {
    const raw = window.localStorage.getItem(key(userId));
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as Partial<LocalProfile> | null;
    if (!parsed || typeof parsed !== 'object') return empty();
    return {
      games_played: Number(parsed.games_played ?? 0) || 0,
      current_streak: Number(parsed.current_streak ?? 0) || 0,
      max_streak: Number(parsed.max_streak ?? 0) || 0,
      high_score: Number(parsed.high_score ?? 0) || 0,
      last_played_date: typeof parsed.last_played_date === 'string' ? parsed.last_played_date : '',
    };
  } catch {
    return empty();
  }
}

export function setLocalProfile(userId: string, profile: LocalProfile) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key(userId), JSON.stringify(profile));
  } catch {
    // ignore write failures
  }
}

export function applyCompletedGameToLocalProfile(userId: string, playDateYmd: string, score: number) {
  const profile = getLocalProfile(userId);
  const today = (playDateYmd || '').slice(0, 10);
  const realToday = formatLocalYmd(new Date());

  const todayDate = parseYmdToLocalDate(today);
  const yesterday = todayDate
    ? formatLocalYmd(new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() - 1))
    : '';

  const lastPlayed = (profile.last_played_date || '').slice(0, 10);

  // If it's an archive game (play date is before today), don't break the streak.
  // Just update games played and high score.
  if (today < realToday) {
    const next: LocalProfile = {
      ...profile,
      games_played: (profile.games_played ?? 0) + 1,
      high_score: Math.max(profile.high_score ?? 0, score),
    };
    setLocalProfile(userId, next);
    return next;
  }

  // Only apply once per calendar day (mirrors previous Supabase behavior).
  if (!today || lastPlayed === today) return profile;

  const prevStreak = profile.current_streak ?? 0;
  const newStreak = lastPlayed === yesterday ? prevStreak + 1 : 1;

  const gamesPlayed = (profile.games_played ?? 0) + 1;
  const maxStreak = Math.max(profile.max_streak ?? 0, newStreak);
  const highScore = Math.max(profile.high_score ?? 0, score);

  const next: LocalProfile = {
    games_played: gamesPlayed,
    current_streak: newStreak,
    max_streak: maxStreak,
    high_score: highScore,
    last_played_date: today,
  };

  setLocalProfile(userId, next);
  return next;
}





